import { useEffect } from "react";
import { Alert, DatePicker, Form, Input, InputNumber, Modal } from "antd";
import dayjs, { type Dayjs } from "dayjs";

import type {
  TargetDraft,
  TrackedTarget,
} from "@/views/TrialEnrollment/enrollmentTarget.types";

interface FormShape {
  month: Dayjs;
  sponsorPlan: number;
  rationale: string;
}

/**
 * Enter or correct one month's planned cumulative enrolment.
 *
 * The value is cumulative, not per-month, which is easy to get wrong, so the form says so
 * and warns when a value would fall below the previous month rather than accepting a curve
 * that goes backwards.
 */
export function EnrollmentTargetModal({
  open,
  editing,
  targets,
  actorName,
  onCancel,
  onSubmit,
}: {
  open: boolean;
  editing: TrackedTarget | null;
  targets: TrackedTarget[];
  actorName: string;
  onCancel: () => void;
  onSubmit: (draft: TargetDraft) => void;
}) {
  const [form] = Form.useForm<FormShape>();
  const month = Form.useWatch("month", form);
  const value = Form.useWatch("sponsorPlan", form);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      form.setFieldsValue({
        month: dayjs(`${editing.month}-01`),
        sponsorPlan: editing.sponsorPlan,
        rationale: "",
      });
    } else {
      form.resetFields();
    }
  }, [open, editing, form]);

  const monthKey = month ? month.format("YYYY-MM") : null;
  const existing = monthKey
    ? targets.find((target) => target.month === monthKey)
    : undefined;
  const previous = monthKey
    ? [...targets]
        .filter((target) => target.month < monthKey)
        .sort((a, b) => a.month.localeCompare(b.month))
        .at(-1)
    : undefined;

  const goesBackwards =
    typeof value === "number" &&
    previous !== undefined &&
    value < previous.sponsorPlan;

  return (
    <Modal
      open={open}
      title={
        editing ? `Edit target — ${editing.month}` : "Add enrolment target"
      }
      okText={editing ? "Save target" : "Add target"}
      width={520}
      onCancel={onCancel}
      onOk={async () => {
        let values: FormShape;
        try {
          values = await form.validateFields();
        } catch {
          return;
        }
        onSubmit({
          month: values.month.format("YYYY-MM"),
          sponsorPlan: values.sponsorPlan,
          rationale: values.rationale,
        });
      }}
      destroyOnHidden
    >
      <p className="mb-4 text-[12px] text-[#6B7280]">
        Recorded against{" "}
        <span className="font-semibold text-[#374151]">{actorName}</span>
      </p>

      <Form form={form} layout="vertical" requiredMark={false}>
        <Form.Item
          name="month"
          label="Month"
          rules={[{ required: true, message: "Pick a month." }]}
        >
          <DatePicker picker="month" className="w-full" disabled={!!editing} />
        </Form.Item>

        {!editing && existing ? (
          <Alert
            className="mb-4"
            type="info"
            showIcon
            title={`${existing.month} already has a target of ${existing.sponsorPlan}`}
            description="Saving will update that month rather than adding a second value for it."
          />
        ) : null}

        <Form.Item
          name="sponsorPlan"
          label="Planned cumulative enrolment"
          rules={[{ required: true, message: "Enter a number." }]}
          extra={
            previous
              ? `Cumulative, not the month's own intake. ${previous.month} is ${previous.sponsorPlan}.`
              : "Cumulative, not the month's own intake."
          }
        >
          <InputNumber className="w-full" min={0} max={100000} />
        </Form.Item>

        {goesBackwards ? (
          <Alert
            className="mb-4"
            type="warning"
            showIcon
            title="This is lower than the month before"
            description={`A cumulative total cannot fall. ${previous?.month} is ${previous?.sponsorPlan}.`}
          />
        ) : null}

        <Form.Item
          name="rationale"
          label={editing ? "Why is this changing?" : "Why this target?"}
          rules={[
            { required: true, message: "A rationale is required." },
            {
              min: 10,
              message: "Give enough detail to be useful in six months.",
            },
          ]}
        >
          <Input.TextArea
            rows={3}
            placeholder="e.g. Two additional sites activating in Q4 raise the ramp."
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}
